import type { TrackingPageOrder } from "@redotech/order-tracking-common/tracking-page-order-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import EditPencilIcon from "@redotech/redo-icons/edit-05.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoOrderTrackingRpcClientContext } from "@redotech/redo-merchant-app-common/order-tracking-rpc-client-provider";
import { TrackingPageThumbnail } from "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/components/tracking-page-thumbnail";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ReactNode, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import * as pickerCss from "./tracking-page-picker.module.css";

const CREATE_OPTION_ID = "__create_tracking_page__";

export interface TrackingPageOption {
  _id: string;
  name: string;
}

/**
 * Dropdown + thumbnail preview for choosing one of the team's tracking pages.
 * Shared between the step's default page section and the scheduled-event
 * editor.
 */
export function TrackingPagePicker({
  trackingPageId,
  setTrackingPageId,
  trackingPages,
  label,
  disabled,
  showRequiredError,
  /**
   * Whether the Edit/Create buttons that navigate to the tracking-page
   * builder are shown. The scheduled-event editor hides them: navigating away
   * would discard the in-progress event draft.
   */
  allowBuilderNavigation,
  stepId,
  save,
  /** Rendered inside the content box instead of the thumbnail when set. */
  replacementContent,
  /** Real order for the thumbnail's data-bound blocks; mock when absent. */
  previewOrder,
  onPageCreated,
}: {
  trackingPageId: string | undefined;
  setTrackingPageId: (id: string) => void;
  trackingPages: TrackingPageOption[] | undefined;
  label: string;
  disabled?: boolean;
  showRequiredError?: boolean;
  allowBuilderNavigation?: boolean;
  stepId: string;
  save?: () => void;
  replacementContent?: ReactNode;
  previewOrder?: TrackingPageOrder;
  /** Called when a new page is created; parent should add it to the shared pages list. */
  onPageCreated?: (page: TrackingPageOption) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const orderTrackingRpcClient = useRequiredContext(
    RedoOrderTrackingRpcClientContext,
  );

  const createInFlight = useRef(false);

  const trackingPageOptions = useMemo<TextRedoListItem<string>[]>(() => {
    const pageItems = (trackingPages ?? []).map((page) => ({
      id: page._id,
      value: page._id,
      type: "text" as const,
      text: page.name,
    }));
    return [
      ...pageItems,
      {
        id: CREATE_OPTION_ID,
        value: CREATE_OPTION_ID,
        type: "text" as const,
        text: t("Create tracking page"),
        leadingItem: { type: "icon" as const, Icon: PlusIcon },
      },
    ];
  }, [trackingPages, t]);

  const selectedOption = useMemo(
    () =>
      trackingPageId === CREATE_OPTION_ID
        ? undefined
        : trackingPageOptions.find((option) => option.value === trackingPageId),
    [trackingPageOptions, trackingPageId],
  );

  const selectedPageLoad = useLoad(async () => {
    if (!trackingPageId) {
      return undefined;
    }
    const page = await orderTrackingRpcClient.getTrackingPageById({
      id: trackingPageId,
    });
    return page?.settings;
  }, [orderTrackingRpcClient, trackingPageId]);

  const page = (trackingPages ?? []).find((p) => p._id === trackingPageId);

  const goToBuilder = async (pageId: string) => {
    if (save) {
      await save();
    }
    const returnPath = `returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}`;
    navigate(
      `../../tracking-page/tracking-page-builder?id=${pageId}&${returnPath}`,
    );
  };

  return (
    <Flex dir="column" gap="2xl" justify="center">
      <Flex dir="column" gap="xxs">
        <Text fontSize="xs" textColor="secondary">
          {label}
        </Text>
        <RedoSingleSelectDropdownInput<string>
          disabled={disabled}
          options={trackingPageOptions}
          optionSelected={({ item }) => {
            if (item.value === CREATE_OPTION_ID) {
              if (createInFlight.current) {
                return;
              }
              createInFlight.current = true;
              orderTrackingRpcClient
                .createTrackingPage({ name: "New Tracking Page" })
                .then(async (newPage) => {
                  const created = {
                    _id: newPage._id,
                    name: "New Tracking Page",
                  };
                  onPageCreated?.(created);
                  setTrackingPageId(newPage._id);
                  if (allowBuilderNavigation) {
                    await goToBuilder(newPage._id);
                  }
                })
                .catch((error) => {
                  console.error("Failed to create tracking page:", error);
                })
                .finally(() => {
                  createInFlight.current = false;
                });
              return;
            }
            const selectedPage = (trackingPages ?? []).find(
              (p) => p._id === item.value,
            );
            if (!selectedPage) {
              return;
            }
            setTrackingPageId(selectedPage._id);
          }}
          placeholder={t("Select a tracking page")}
          selectedItem={selectedOption}
          size="xs"
        />
      </Flex>

      <Flex dir="column" gap="xxs">
        <Flex align="center" dir="row" justify="space-between" mb="md">
          <Text fontSize="xs" textColor="secondary">
            {t("Content")}
          </Text>
          {allowBuilderNavigation && page && (
            <RedoButton
              disabled={disabled}
              hierarchy="secondary"
              IconLeading={EditPencilIcon}
              onClick={() => goToBuilder(page._id)}
              size="xs"
              text={t("Edit")}
            />
          )}
        </Flex>
        <Flex
          bgColor="primary"
          borderColor={!page && showRequiredError ? "error" : "primary"}
          borderRadius="md"
          borderStyle="solid"
          borderWidth="1px"
          className={pickerCss.previewContainer}
          dir="column"
          p="sm"
          position="relative"
        >
          {replacementContent ? (
            replacementContent
          ) : page ? (
            <Flex
              borderRadius="md"
              dir="column"
              height="full"
              overflow="hidden"
            >
              <TrackingPageThumbnail
                order={previewOrder}
                settings={selectedPageLoad.value ?? undefined}
              />
            </Flex>
          ) : (
            <Flex
              align="center"
              bgColor="primary"
              className={pickerCss.previewContainer}
              height="full"
              justify="center"
            >
              {allowBuilderNavigation ? (
                <RedoButton
                  disabled={disabled}
                  hierarchy="secondary"
                  IconLeading={PlusIcon}
                  onClick={async () => {
                    try {
                      const newPage =
                        await orderTrackingRpcClient.createTrackingPage({
                          name: "New Tracking Page",
                        });
                      setTrackingPageId(newPage._id);
                      await goToBuilder(newPage._id);
                    } catch (error) {
                      console.error("Failed to create tracking page:", error);
                    }
                  }}
                  text={t("Create tracking page")}
                  theme="action"
                />
              ) : (
                <Text fontSize="xs" textColor="secondary">
                  {t("Select a tracking page to preview it")}
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
